import { useContext, useEffect, useState, useCallback } from 'react';
import axios from '../utils/axios';
import { REACT_APP_UPLOADS } from '../constants';
import { AuthContext } from '../contexts/Auth/Auth.Context';

/**
 * Manages the current user's tracked seizure cases.
 * - Fetches tracked case IDs on mount (authenticated users only).
 * - `toggle(caseId)` tracks or untracks a case optimistically.
 * - Returns empty Set + no-op toggle for guests.
 *
 * NOTE: toggle uses functional state updaters throughout so it never
 * captures stale snapshots of trackedIds / toggling in its closure.
 */
export function useTrackedCases() {
  const { state } = useContext(AuthContext);
  const [trackedIds, setTrackedIds] = useState<Set<number>>(new Set());
  const [toggling,   setToggling]   = useState<Set<number>>(new Set());

  const isLoggedIn = !!state.userToken;

  useEffect(() => {
    if (!isLoggedIn) { setTrackedIds(new Set()); return; }
    axios
      .get(`${REACT_APP_UPLOADS}/api/v1/case-tracking/my-cases`, {
        headers: { Authorization: `Bearer ${state.userToken}` },
      })
      .then((res) => {
        if (Array.isArray(res.data)) {
          // Normalise to numbers — backend may return integers or strings
          setTrackedIds(new Set(res.data.map(Number)));
        }
      })
      .catch((_err) => { /* non-critical — user sees empty tracked set */ });
  }, [isLoggedIn, state.userToken]);

  const toggle = useCallback(
    async (rawId: number | string) => {
      if (!isLoggedIn) return;

      const caseId = Number(rawId);

      // Debounce — bail if already in-flight. Use functional updater so we
      // read the *latest* toggling set, not one captured in the closure.
      let alreadyToggling = false;
      setToggling((prev) => {
        if (prev.has(caseId)) { alreadyToggling = true; return prev; }
        return new Set(Array.from(prev).concat(caseId));
      });
      if (alreadyToggling) return;

      // Read current tracked state AND apply optimistic update atomically.
      let wasTracked = false;
      setTrackedIds((prev) => {
        wasTracked = prev.has(caseId);
        const next = new Set(prev);
        wasTracked ? next.delete(caseId) : next.add(caseId);
        return next;
      });

      try {
        if (wasTracked) {
          await axios.delete(`${REACT_APP_UPLOADS}/api/v1/case-tracking/${caseId}`, {
            headers: { Authorization: `Bearer ${state.userToken}` },
          });
        } else {
          await axios.post(
            `${REACT_APP_UPLOADS}/api/v1/case-tracking/${caseId}`,
            {},
            { headers: { Authorization: `Bearer ${state.userToken}` } },
          );
        }
      } catch {
        // Revert optimistic update on failure
        setTrackedIds((prev) => {
          const next = new Set(prev);
          wasTracked ? next.add(caseId) : next.delete(caseId);
          return next;
        });
      } finally {
        setToggling((prev) => { const s = new Set(prev); s.delete(caseId); return s; });
      }
    },
    // No trackedIds / toggling in deps — we use functional updaters instead
    [isLoggedIn, state.userToken],
  );

  return { trackedIds, toggle, isLoggedIn };
}
